/**
 * Deforum Schedule Editor
 *
 * Configuration panel for Deforum (local ComfyUI) schedule-driven animation.
 * Two tabs: Basics (model/fps/cadence/resolution), Sampler (steps/cfg).
 *
 * Camera schedules (zoom, translation, rotation, strength, noise) are
 * fully LLM-controlled via DeforumCurator — not user-editable.
 *
 * All strings go through i18n (RULE #12); dark-theme only (bg-black/30,
 * text-white/70); mobile-first responsive (44px touch targets).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import type { CheckpointInfo } from '@/services/civitaiApi';
import { type LoraInfo, listInstalledLoras } from '@/services/loraApi';
import { CheckpointPicker } from './CheckpointPicker';
import {
  DEFAULT_DEFORUM_CONFIG,
  deriveOrientation,
  DEFORUM_CADENCE_HIGH_DEGRADATION_THRESHOLD,
  DEFORUM_CADENCE_SEVERE_DEGRADATION_THRESHOLD,
  DEFORUM_MAX_CADENCE,
  DEFORUM_MIN_CADENCE,
  DEFORUM_SAMPLER_OPTIONS,
  DEFORUM_SCHEDULER_OPTIONS,
  resolveDeforumResolution,
  type DeforumConfig,
} from './constants';
import { HyperSpeedSlider, type HyperSteps } from './HyperSpeedSlider';
import { LoraStack } from './LoraStack';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Heuristic: infer checkpoint base model from filename for HyperSpeedSlider
 * and LoraStack compatibility filtering.
 */
function inferBaseModelFromCheckpoint(
  filename: string | undefined
): 'SD 1.5' | 'SD 1.5 Hyper' | 'SDXL 1.0' {
  // Guard: persisted deforum_config from an older schema may omit checkpoint_filename.
  const lower = (filename ?? '').toLowerCase();
  if (lower.includes('sdxl') || lower.includes('xl')) return 'SDXL 1.0';
  if (lower.includes('hyper')) return 'SD 1.5 Hyper';
  return 'SD 1.5';
}

/**
 * Heuristic: is this checkpoint ALREADY a distilled fast model (Lightning,
 * Hyper, LCM, Turbo)? Such checkpoints must NOT also use Speed mode (Hyper-SD
 * LoRA) — stacking a second distillation degrades output ("double distillation")
 * and the LoRA's step count would override the checkpoint's recommended steps.
 * Works from the filename so it survives a reload (config carries no meta).
 */
function isDistilledCheckpoint(filename: string | undefined): boolean {
  const lower = (filename ?? '').toLowerCase();
  return (
    lower.includes('lightning') ||
    lower.includes('hyper') ||
    lower.includes('lcm') ||
    lower.includes('turbo')
  );
}

// ============================================================================
// Component
// ============================================================================

interface Props {
  config: DeforumConfig;
  onChange: (next: DeforumConfig) => void;
  disabled?: boolean;
  /** User topic string — passed to CheckpointPicker for recommendation hint. */
  topic?: string;
  /** Target video resolution, e.g. "1920x1080" — drives Deforum's render
   * orientation (horizontal/vertical/square) so the generated clip isn't a
   * mismatched square that gets wastefully cropped downstream. */
  targetResolution?: string;
}

type Tab = 'basics' | 'sampler';

export function DeforumScheduleEditor({
  config: configProp = DEFAULT_DEFORUM_CONFIG,
  onChange,
  disabled = false,
  topic,
  targetResolution,
}: Props) {
  // Merge over defaults so a persisted config from an older schema (missing
  // sampler/scheduler or other newer fields) is upgraded transparently rather
  // than feeding `undefined` into controlled inputs.
  const config: DeforumConfig = useMemo(
    () => ({ ...DEFAULT_DEFORUM_CONFIG, ...configProp }),
    [configProp]
  );
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('basics');
  const [installedLoras, setInstalledLoras] = useState<LoraInfo[]>([]);

  // Load installed LoRAs once on mount
  useEffect(() => {
    listInstalledLoras()
      .then(setInstalledLoras)
      .catch(() => {
        // Non-fatal — empty list means picker shows nothing until user browses CivitAI
      });
  }, []);

  const set = useCallback(
    <K extends keyof DeforumConfig>(key: K, value: DeforumConfig[K]) =>
      onChange({ ...config, [key]: value }),
    [config, onChange]
  );

  const orientation = deriveOrientation(targetResolution);

  const handleCheckpointChange = useCallback(
    (filename: string, base_model: 'sd15' | 'sdxl', is_hyper: boolean, meta?: CheckpointInfo) => {
      // Apply the model's recommended sampler settings on selection (mirrors
      // ImageConfigForm's meta.recommended_* handling). Every checkpoint change
      // RESETS steps/CFG/sampler/scheduler to that model's correct values so
      // switching Lightning <-> normal never leaves stale fast-render settings:
      //   - curated catalog provides recommended_* (Lightning: ~6 steps, CFG 2,
      //     DPM++ SDE Karras; Hyper: ~4 steps, CFG 1.5),
      //   - else the Hyper heuristic (external Hyper checkpoints w/o a spec),
      //   - else the plain Deforum defaults (25 steps, CFG 7.5, euler/normal).
      const steps =
        meta?.recommended_steps ?? (is_hyper ? 4 : DEFAULT_DEFORUM_CONFIG.num_inference_steps);
      const cfg = meta?.recommended_cfg ?? (is_hyper ? 1.5 : DEFAULT_DEFORUM_CONFIG.guidance_scale);
      const sampler = meta?.recommended_sampler ?? DEFAULT_DEFORUM_CONFIG.sampler;
      const scheduler = meta?.recommended_scheduler ?? DEFAULT_DEFORUM_CONFIG.scheduler;
      // A distilled checkpoint (Lightning/Hyper) must NOT stack a Hyper-SD LoRA
      // on top (double distillation + the LoRA step count would override the
      // recommended steps). Force Speed mode back to Normal so Sampling steps is
      // the single, honest source of the step count.
      const distilled =
        is_hyper || meta?.recommended_steps != null || isDistilledCheckpoint(filename);
      const hyper_steps: HyperSteps = distilled ? 0 : (config.hyper_steps ?? 0);
      // SDXL was trained at ~1024x1024; SD 1.5 at 512x512. Render resolution is
      // otherwise hardcoded and has no UI control, so it must be derived here —
      // rendering an SDXL checkpoint at the SD1.5-scale default starves it of
      // resolution and produces generic/wrong compositions and malformed
      // subjects regardless of the prompt. Deforum/SD are not square-only —
      // match the target video's orientation instead of always squaring it,
      // which avoids a wasteful scale-to-cover + center-crop downstream.
      const { width, height } = resolveDeforumResolution(base_model, orientation);
      onChange({
        ...config,
        checkpoint_filename: filename,
        base_model,
        num_inference_steps: steps,
        guidance_scale: cfg,
        sampler,
        scheduler,
        hyper_steps,
        width,
        height,
      });
    },
    [config, onChange, orientation]
  );

  const checkpointBaseModel = inferBaseModelFromCheckpoint(config.checkpoint_filename);
  const hyperSteps = (config.hyper_steps ?? 0) as HyperSteps;
  const isFastMode = hyperSteps !== 0;
  // Lightning/Hyper checkpoints are already distilled — Speed mode (Hyper-SD
  // LoRA) is incompatible (double distillation), so it is locked to Normal.
  const checkpointIsDistilled = isDistilledCheckpoint(config.checkpoint_filename);

  // Self-heal an invalid persisted/preset combo. handleCheckpointChange forces
  // Speed mode OFF when you actively pick a distilled checkpoint, but a config
  // restored from persistence (or a preset) can arrive as "distilled checkpoint
  // + hyper_steps != 0". That combo is double-distillation AND it DISABLES the
  // editable Sampling-steps field (hyperActive). Reset hyper_steps to 0 so the
  // steps input stays user-editable (defaulted to the model's recommended value)
  // and the bad LoRA pairing is never sent to the backend. Guarded so it fires
  // once and cannot loop.
  useEffect(() => {
    if (checkpointIsDistilled && (config.hyper_steps ?? 0) !== 0) {
      onChange({ ...config, hyper_steps: 0 });
    }
  }, [checkpointIsDistilled, config, onChange]);

  // Same double-distillation hazard, via the manual LoRA stack instead of
  // Speed mode: a distilled checkpoint (Lightning/Hyper) + an explicit
  // Hyper-SD speed LoRA in config.loras stacks two distillations. Deforum
  // re-feeds each rendered frame into the next as the img2img init image, so
  // the extra over-denoising compounds frame over frame and the clip "fries"
  // into a fixed oversaturated pattern within 1-2 seconds. Self-heal once we
  // know which installed LoRAs are flagged is_hyper_speed_lora (mirrors the
  // hyper_steps self-heal above).
  useEffect(() => {
    if (!checkpointIsDistilled || installedLoras.length === 0 || !config.loras?.length) return;
    const speedLoraFilenames = new Set(
      installedLoras.filter((l) => l.is_hyper_speed_lora).map((l) => l.filename)
    );
    if (config.loras.some((entry) => speedLoraFilenames.has(entry.filename))) {
      onChange({
        ...config,
        loras: config.loras.filter((entry) => !speedLoraFilenames.has(entry.filename)),
      });
    }
  }, [checkpointIsDistilled, installedLoras, config, onChange]);

  // Self-heal width/height whenever they drift from what (base_model,
  // orientation) resolves to: a persisted config left at the SD1.5-scale
  // default after switching to an SDXL checkpoint, an older schema without
  // orientation-aware resolution at all, or the user changing the target
  // video's resolution (horizontal <-> vertical <-> square) after Deforum's
  // resolution was already set for the previous orientation. There is no UI
  // control for width/height, so any mismatch is definitionally stale.
  useEffect(() => {
    const expected = resolveDeforumResolution(config.base_model, orientation);
    if (config.width !== expected.width || config.height !== expected.height) {
      onChange({ ...config, width: expected.width, height: expected.height });
    }
  }, [config, onChange, orientation]);

  return (
    <div className="card bg-black/30 backdrop-blur-xl p-3 sm:p-4 md:p-6 rounded-xl border border-white/10">
      {/* Checkpoint picker — shown above all schedule controls */}
      <div className="mb-4">
        <CheckpointPicker
          value={config.checkpoint_filename}
          onChange={handleCheckpointChange}
          topic={topic}
          disabled={disabled}
        />
      </div>

      {/* Fast render mode badge — visible even when Advanced is collapsed */}
      {isFastMode && (
        <div className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-900/30 border border-green-500/30 rounded-full text-xs text-green-300 font-medium">
          <span aria-hidden>&#9889;</span>
          {t('deforum.advanced.fastRenderBadge')}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-4 border-b border-white/10">
        {(['basics', 'sampler'] as Tab[]).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            disabled={disabled}
            className={`min-h-[44px] px-4 text-sm font-medium transition-colors ${
              tab === id
                ? 'text-white border-b-2 border-purple-400'
                : 'text-white/60 hover:text-white/80'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {t(`deforum.editor.tab.${id}`)}
          </button>
        ))}
      </div>

      {tab === 'basics' && <BasicsTab config={config} set={set} disabled={disabled} />}
      {tab === 'sampler' && <SamplerTab config={config} set={set} disabled={disabled} />}

      {/* Advanced — Speed & LoRAs disclosure (collapsed by default) */}
      <details className="bg-black/30 border border-white/10 rounded-lg p-4 mt-3 mb-1">
        <summary className="cursor-pointer text-white/80 font-medium select-none">
          {t('deforum.advanced.sectionTitle')}
        </summary>
        <div className="mt-4 space-y-4">
          {/* Speed mode (Hyper-SD LoRA) only applies to a NON-distilled
              checkpoint. A Lightning/Hyper checkpoint is already distilled, so
              stacking a Hyper-SD LoRA is double-distillation — we hide the
              control entirely (rather than show a disabled one) and steer the
              user to the Sampling steps field in the Sampler tab. */}
          {checkpointIsDistilled ? (
            <p className="text-xs text-white/50">
              {t('deforum.advanced.speedMode.distilledLocked')}
            </p>
          ) : (
            <HyperSpeedSlider
              value={hyperSteps}
              onChange={(v) => onChange({ ...config, hyper_steps: v })}
              checkpointBaseModel={checkpointBaseModel}
              disabled={disabled}
            />
          )}
          <LoraStack
            value={config.loras ?? []}
            onChange={(loras) => onChange({ ...config, loras })}
            installedLoras={installedLoras}
            checkpointBaseModel={checkpointBaseModel}
            checkpointIsDistilled={checkpointIsDistilled}
          />
        </div>
      </details>
    </div>
  );
}

type SetFn = <K extends keyof DeforumConfig>(k: K, v: DeforumConfig[K]) => void;

function BasicsTab({
  config,
  set,
  disabled,
}: {
  config: DeforumConfig;
  set: SetFn;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {/* Base model is DERIVED from the selected checkpoint (single source of
          truth, RULE #-2) — shown read-only so it can never drift out of sync
          with the checkpoint and silently break generation. */}
      <div>
        <label className="form-label">{t('deforum.editor.baseModel')}</label>
        <div className="input bg-black/20 text-white/70 border-white/10 min-h-[44px] w-full flex items-center px-3 text-sm rounded-lg">
          {config.base_model === 'sdxl'
            ? t('deforum.editor.baseModelSDXL')
            : t('deforum.editor.baseModelSD15')}
        </div>
        <p className="text-xs text-white/40 mt-1">{t('deforum.editor.baseModelDerivedHint')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="form-label">{t('deforum.editor.fps')}</label>
          <input
            type="number"
            min={1}
            max={1000}
            className="input bg-black/20 text-white border-white/10 min-h-[44px] w-full"
            value={config.fps}
            onChange={(e) => set('fps', parseInt(e.target.value, 10) || 12)}
            disabled={disabled}
          />
        </div>
        <div>
          <label className="form-label">{t('deforum.editor.cadence')}</label>
          <input
            type="number"
            min={DEFORUM_MIN_CADENCE}
            max={DEFORUM_MAX_CADENCE}
            className="input bg-black/20 text-white border-white/10 min-h-[44px] w-full"
            value={config.cadence}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n)) {
                set('cadence', Math.min(DEFORUM_MAX_CADENCE, Math.max(DEFORUM_MIN_CADENCE, n)));
              }
            }}
            disabled={disabled}
          />
          <p className="text-xs text-white/40 mt-1">{t('deforum.editor.cadenceHint')}</p>
          {config.cadence >= DEFORUM_CADENCE_SEVERE_DEGRADATION_THRESHOLD ? (
            <p className="text-xs text-red-300/80 mt-1">
              {t('deforum.editor.cadenceWarningSevere')}
            </p>
          ) : config.cadence >= DEFORUM_CADENCE_HIGH_DEGRADATION_THRESHOLD ? (
            <p className="text-xs text-amber-300/80 mt-1">
              {t('deforum.editor.cadenceWarningHigh')}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SamplerTab({
  config,
  set,
  disabled,
}: {
  config: DeforumConfig;
  set: SetFn;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  // When Speed mode (Hyper-SD) is active, the provider OVERRIDES both steps and
  // CFG with the LoRA's fixed values — so these fields would lie. Lock them and
  // show the effective step count to answer "which value is actually used?".
  const hyperActive = (config.hyper_steps ?? 0) !== 0;
  return (
    <div className="space-y-3">
      <div>
        <label className="form-label">{t('deforum.editor.steps')}</label>
        <input
          type="number"
          min={1}
          max={60}
          className="input bg-black/20 text-white border-white/10 min-h-[44px] w-full disabled:opacity-50"
          value={hyperActive ? (config.hyper_steps ?? 0) : config.num_inference_steps}
          onChange={(e) => {
            // Keep the current value while the field is mid-edit/empty instead of
            // snapping to a hardcoded 25 — that snap made it impossible to clear
            // "2" and type "8" (it jumped to 25 the instant the box went empty).
            const n = parseInt(e.target.value, 10);
            if (!Number.isNaN(n)) set('num_inference_steps', n);
          }}
          disabled={disabled || hyperActive}
        />
      </div>
      <div>
        <label className="form-label">{t('deforum.editor.cfgScale')}</label>
        <input
          type="number"
          step="0.5"
          min={1}
          max={20}
          className="input bg-black/20 text-white border-white/10 min-h-[44px] w-full disabled:opacity-50"
          value={config.guidance_scale}
          onChange={(e) => {
            // Same fix as steps: don't snap to 7.5 the instant the field is empty.
            const n = parseFloat(e.target.value);
            if (!Number.isNaN(n)) set('guidance_scale', n);
          }}
          disabled={disabled || hyperActive}
        />
      </div>
      {hyperActive && (
        <p className="text-xs text-amber-300/80">{t('deforum.editor.hyperOverrideHint')}</p>
      )}

      {/* Sampler + scheduler. Auto-set from the checkpoint on selection
          (Lightning => DPM++ SDE + Karras); editable here for advanced users. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="form-label">{t('deforum.editor.sampler')}</label>
          <select
            className="select bg-black/20 text-white border-white/10 min-h-[44px] w-full"
            value={config.sampler}
            onChange={(e) => set('sampler', e.target.value)}
            disabled={disabled}
          >
            {DEFORUM_SAMPLER_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label">{t('deforum.editor.scheduler')}</label>
          <select
            className="select bg-black/20 text-white border-white/10 min-h-[44px] w-full"
            value={config.scheduler}
            onChange={(e) => set('scheduler', e.target.value)}
            disabled={disabled}
          >
            {DEFORUM_SCHEDULER_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="text-xs text-white/40">{t('deforum.editor.samplerHint')}</p>
    </div>
  );
}
